import * as React from "react";
import { Query, Mutation } from "react-apollo";
import { Form, Label, FormGroup, Input, Button } from "reactstrap";
import Select from "react-select";

// GraphQL
import UpdatePartMutation from "../../GraphQL/Mutations/UpdatePart";
import GetCompanyParts from "../../GraphQL/Queries/GetCompanyParts";
import GetCompanySites from "../../GraphQL/Queries/GetCompanySites";

// Presentation/UI
import Loader from "../../Components/Loader";

// Theme
import { Colors } from "../../Themes";

interface IProps {
  companyId: any;
  part: any;
  closeModal(): void;
  notification(message: string): void;
}

interface IState {
  id: string;
  name: string;
  description: string;
  category: string;
  quantity: string;
  barcode: string;
  additionalPartDetails: string;
  loading: boolean;
  site: any;
  siteId: any;
  error: null | string;
}

class EditPartForm extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      id: props.part.id,
      name: props.part.name,
      description: props.part.description,
      category: props.part.category,
      quantity: props.part.quantity,
      barcode: props.part.barcode,
      additionalPartDetails: props.part.additionalPartDetails,
      site: props.part.site && {
        id: props.part.site.id,
        value: props.part.site,
        label: props.part.site.name
      },
      loading: false,
      siteId: !props.part.site ? null : props.part.site.id,
      error: null
    };
  }

  // Handle change
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "partName":
        this.setState({ name: value });
        break;
      case "description":
        this.setState({ description: value });
        break;
      case "category":
        this.setState({ category: value });
        break;
      case "quantity":
        this.setState({ quantity: value });
        break;
      case "barcode":
        this.setState({ barcode: value });
        break;
      case "additionalPartDetails":
        this.setState({ additionalPartDetails: value });
        break;
      default:
        break;
    }
  };

  // Select site
  selectSite = (site: any) => {
    const siteId = site.value.id;
    this.setState({ siteId, site });
  };

  // Return sites
  returnSites = (
    sites: Array<any>
  ): Array<{ value: object; label: string }> => {
    return sites.map((site: any) => {
      return {
        value: site,
        label: `${site.name}`
      };
    });
  };

  validateForm = (): boolean => {
    // Check for undefined or empty infput fields
    if (!this.state.name) {
      this.setError("Please fill in the name of the part");
      return false;
    }
    return true;
  };

  // Error
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  render() {
    const {
      id,
      name,
      description,
      category,
      quantity,
      barcode,
      additionalPartDetails,
      site,
      siteId
    } = this.state;
    const { companyId, closeModal, notification } = this.props;
    return (
      <Query query={GetCompanySites} variables={{ id: companyId }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Loader />;
          }
          if (error) {
            return <div>There was a problem loading the data</div>;
          }

          const sites = data.allSites;

          return (
            <Mutation<{}, {}> mutation={UpdatePartMutation}>
              {updateSite => (
                <Form
                  onSubmit={e => {
                    e.preventDefault();
                    if (this.validateForm()) {
                      this.setState({ loading: true });

                      updateSite({
                        variables: {
                          id,
                          name,
                          description,
                          category,
                          quantity,
                          barcode,
                          additionalPartDetails,
                          siteId
                        },
                        refetchQueries: [
                          {
                            query: GetCompanyParts,
                            variables: { id: companyId }
                          }
                        ]
                      })
                        .then(res => {
                          this.setState({ loading: false });

                          closeModal();
                          notification("Part updated successfully");
                        })
                        .catch(err => {
                          this.setState({ loading: false });
                        });
                    }
                  }}
                >
                  <FormGroup>
                    <Label for="partName">Part Name</Label>
                    <Input
                      type="text"
                      name="partName"
                      id="partName"
                      value={name}
                      placeholder="Part name"
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="description">Description</Label>
                    <Input
                      type="textarea"
                      name="description"
                      id="description"
                      value={description}
                      placeholder="Description"
                      onChange={this.handleChange}
                      style={{ resize: "none" }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="category">Category</Label>
                    <Input
                      type="text"
                      name="category"
                      id="category"
                      value={category}
                      placeholder="Category"
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Site</Label>
                    <Select
                      onChange={this.selectSite}
                      options={this.returnSites(sites)}
                      value={site}
                      isSearchable={true}
                      placeholder="Sites"
                      className="select-styling"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="barcode">Barcode</Label>
                    <Input
                      type="text"
                      name="barcode"
                      id="barcode"
                      value={barcode}
                      placeholder="Barcode"
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="additionalPartDetails">
                      Additional Part Details
                    </Label>
                    <Input
                      type="textarea"
                      name="additionalPartDetails"
                      id="additionalPartDetails"
                      value={additionalPartDetails}
                      placeholder="Additional part details"
                      onChange={this.handleChange}
                      style={{ resize: "none" }}
                    />
                  </FormGroup>
                  <br />
                  <FormGroup>
                    <Button>
                      {!this.state.loading ? <span>Update</span> : <Loader />}
                    </Button>
                  </FormGroup>
                  {this.state.error && (
                    <p style={{ textAlign: "center", color: Colors.error }}>
                      {this.state.error}
                    </p>
                  )}
                </Form>
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

export default EditPartForm;



// WEBPACK FOOTER //
// ./src/Containers/Parts/EditPartForm.tsx